import React, { useEffect } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from "react-toastify";
import { createMessage, updateMessage, deleteMessage, getCampaignEchos } from '../../../../../redux/thunks/emailCampaignThunk';
import { selectDurationOptions } from '../../../../../redux/reducers/durationReducer';
import { CustomButton, CustomReactSelect } from '../../../../shared';

const initialValue = {
    echoName: '',
    echoDuration: '',
    echoType: '0',
    echoMessage: '',
    echoSubject: '',
};

const emailEchoSchema = yup
    .object({
        echoDuration: yup.string().required('Please Select Echo Duration'),
        echoType: yup.string().required('Please Select Echo Type'),
        echoMessage: yup.string().required('Message is required'),
        echoSubject: yup.string().required('Subject is required'),
    })
    .required();

const ECHO_TYPE = [
    { label: 'Non Engaged', value: "0" },
    { label: 'Engaged', value: "1" },
    { label: 'Default', value: "2" },
];

function EmailEchoBuilder(props) {
    const { id, subject, message, duration, type, handleAfterEchoSave, handleEchoRowState } = props;
    const dispatch = useDispatch();
    const echoDurations = useSelector(selectDurationOptions());
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(emailEchoSchema),
        defaultValues: initialValue,
    });

    const {
        field: {
            value: echoDuration,
            onChange: onEchoDurationChange,
            ...restEchoDurationProps
        },
    } = useController({ name: 'echoDuration', control });
    const {
        field: {
            value: echoType,
            onChange: onEchoTypeChange,
            ...restEchoTypeProps
        },
    } = useController({ name: 'echoType', control });

    const handleDurationChange = (selectedOption) => {
        onEchoDurationChange(
            selectedOption ? selectedOption.value : selectedOption
        );
    };

    const handleTypeChange = (selectedOption) => {
        onEchoTypeChange(
            selectedOption ? selectedOption.value : selectedOption
        );
    };

    const resetEchoBuilderFields = () => {
        setValue('echoDuration', "");
        setValue('echoType', "");
        setValue('echoMessage', "");
        setValue('echoSubject', "");
    };

    const onSubmit = (data) => {
        let emailEchoData = {
            duration: data.echoDuration,
            message: data.echoMessage,
            engagement_enabled: data.echoType,
            subject: data.echoSubject,
        }
        if(id){
            dispatch(updateMessage({
                companyId: company.id,
                campaignId: campaign.id,
                messageId: id,
                data: emailEchoData,
            })).then((result) => {
                handleEchoRowState();
                resetEchoBuilderFields();
                dispatch(getCampaignEchos({
                    companyId: company.id,
                    campaignId: campaign.id,
                }));
            }).catch((error) => {
                toast.error(error);
            });
        } else {
            dispatch(createMessage({
                companyId: company.id,
                campaignId: campaign.id,
                messageId: id,
                data: emailEchoData,
            })).then((result) => {
                resetEchoBuilderFields();
                handleAfterEchoSave();
                dispatch(getCampaignEchos({
                    companyId: company.id,
                    campaignId: campaign.id,
                }));
            }).catch((error) => {
                toast.error(error);
            });
        }
        
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleDelete = (id) => {
        dispatch(deleteMessage({
            companyId: company.id,
            campaignId: campaign.id,
            messageId: id,
        })).then((result) => {
            resetEchoBuilderFields();
            dispatch(getCampaignEchos({
                companyId: company.id,
                campaignId: campaign.id,
            }));
        }).catch((error) => {
            toast.error(error);
        });
    };

    useEffect(()=> {
        if(id){
            setValue('echoDuration', duration);
            setValue('echoType', type);
            setValue('echoMessage', message);
            setValue('echoSubject', subject);
        }
    },[]);

    return (
        <Grid container>
            <form
                id="emailEchoRow"
                onSubmit={handleSubmit(onSubmit)}
                className="w-100"
            >
                <Grid item xs={12}>
                    {/* Email Echo */}
                    <Box className="padding-12px">
                        <Grid container spacing="12px">
                            {/* Email Echo Duration */}
                            <Grid item xs={6}>
                                <CustomReactSelect
                                    options={echoDurations}
                                    placeholder="Select Echo Duration"
                                    handleFilter={(option) =>
                                        handleDurationChange(option)
                                    }
                                    selectedOption={
                                        echoDuration
                                            ? echoDurations.find(
                                                  (x) =>
                                                      x.value === echoDuration
                                              )
                                            : echoDuration
                                    }
                                    {...restEchoDurationProps}
                                />
                                <span className="text-xs-regular error-500">
                                    {errors.echoDuration?.message}
                                </span>
                            </Grid>
                            {/* Email Echo Type */}
                            <Grid item xs={6}>
                                <CustomReactSelect
                                    options={ECHO_TYPE}
                                    placeholder="Select Echo Type"
                                    handleFilter={(option) =>
                                        handleTypeChange(option)
                                    }
                                    selectedOption={
                                        echoType
                                            ? ECHO_TYPE.find(
                                                  (x) => x.value === echoType
                                              )
                                            : echoType
                                    }
                                    {...restEchoTypeProps}
                                />
                                <span className="text-xs-regular error-500">
                                    {errors.echoType?.message}
                                </span>
                            </Grid>
                            {/* Email Echo Subject */}
                            <Grid item xs={12}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        placeholder="Echo Subject"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('echoSubject')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.echoSubject?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* Email Echo Message */}
                            <Grid item xs={12}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <textarea
                                        placeholder="Echo Message"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('echoMessage')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.echoMessage?.message}
                                    </span>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box className="padding-12px pt-0 flex-row gap-4">
                        <CustomButton
                            title="Save"
                            titleColor="#FFFFFF"
                            buttonColor="#AD509E"
                            handleClick={handleSave}
                            type="submit"
                        />

                        {id && <CustomButton
                            title="Delete"
                            titleColor="#FFFFFF"
                            buttonColor="#FDA29B"
                            handleClick={() => handleDelete(id)}
                        />}
                    </Box>
                </Grid>
            </form>
        </Grid>
    );
};

EmailEchoBuilder.defaultProps = {
    id:null,
    subject:"",
    message:"",
    duration:"", 
    type:"",
    handleAfterEchoSave: () => {},
    handleEchoRowState: () => {},
};

export default EmailEchoBuilder;
